import { ILsRagMessage, ILsRagSession } from '../../../libs/services/LifeSciencesRagService/LifeSciencesRagService';
import { AppSettings } from '../../../AppSettings';
import { RoutingMode, IEstimateQueryResponse } from '../../../libs/models/GenAIModels';
import { AGENTS } from './AgentsSidebar';

export interface ICopilotAgentsProps {
  open: boolean;
  onClose: () => void;
  embedded?: boolean;
}

// Must include `userid` — /api/chat, /api/chat/estimate, and /api/sessions
// all key sessions off the `userid` header (falling back to "anonymous"/""
// when absent), same as LifeSciencesRagService.ts's getHeaders(). Without
// it, a session gets created under "anonymous" but the session-list call's
// separate "" fallback never matches it, so newly-created sessions silently
// never appear in the sidebar even though the chat turn itself succeeds.
const _skillsEngineHeaders = (): HeadersInit => {
  const token  = localStorage.getItem('accessToken') || '';
  const userId = localStorage.getItem('userId') || '';
  return {
    'Content-Type': 'application/json',
    userid: userId,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const _skillsEngineHistory = (history: ILsRagMessage[]) =>
  history
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({ role: m.role, content: m.content }));

export const _skillsEngineChat = async (
  agentId: string,
  prompt: string,
  history: ILsRagMessage[],
  sessionId?: string | null,
  model?: string,
  routingMode?: RoutingMode,
  disableCache?: boolean,
): Promise<{ text: string; model: string; tier?: string; cacheHit?: boolean; cost?: number; session_id: string }> => {
  const body: Record<string, unknown> = {
    prompt,
    agent: agentId,
    history: _skillsEngineHistory(history),
    max_iterations: 20,
  };
  if (sessionId) body.session_id = sessionId;
  if (model) body.model = model;
  if (routingMode) body.routing_mode = routingMode;
  if (disableCache) body.disable_cache = disableCache;

  const engineBase = AppSettings.DCABackendServer || '';
  const resp = await fetch(`${engineBase}/api/chat`, {
    method: 'POST',
    headers: _skillsEngineHeaders(),
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`engine ${resp.status}: ${detail || resp.statusText}`);
  }
  const json = await resp.json();
  return {
    text: json.text || '', model: json.model || agentId, tier: json.tier,
    cacheHit: json.cache_hit, cost: json.cost, session_id: json.session_id || '',
  };
};

// Pre-send cost estimate for a Skills Engine agent turn — mirrors
// LifeSciencesRagService.estimateQuery, just against /api/chat/estimate
// (agent-aware tiered routing) instead of /ls-rag/estimate (RAG-specific).
export const _skillsEngineEstimate = async (
  agentId: string,
  prompt: string,
  history: ILsRagMessage[],
  disableCache?: boolean,
  model?: string,
  routingMode?: RoutingMode,
): Promise<IEstimateQueryResponse> => {
  const body: Record<string, unknown> = {
    prompt,
    agent: agentId,
    history: _skillsEngineHistory(history),
  };
  if (disableCache) body.disable_cache = disableCache;
  if (model) body.model = model;
  if (routingMode) body.routing_mode = routingMode;

  const engineBase = AppSettings.DCABackendServer || '';
  const resp = await fetch(`${engineBase}/api/chat/estimate`, {
    method: 'POST',
    headers: _skillsEngineHeaders(),
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`engine ${resp.status}: ${detail || resp.statusText}`);
  }
  return resp.json() as Promise<IEstimateQueryResponse>;
};

// Session list/create for a Skills Engine agent — mirrors
// LifeSciencesRagService.listSessions/createSession, just agent-parameterized
// (/api/sessions) instead of hardcoded to ls-rag (/ls-rag/sessions), so each
// agent's sidebar history is its own instead of always showing ls-rag's.
// Delete and message-fetch stay on LifeSciencesRagService's existing
// /ls-rag/sessions/<id> routes — those aren't agent-scoped server-side (a
// session is looked up by id alone), so they already work for any agent.
export const _skillsEngineListSessions = async (
  agentId: string,
): Promise<{ sessions: ILsRagSession[]; count: number }> => {
  const engineBase = AppSettings.DCABackendServer || '';
  const resp = await fetch(`${engineBase}/api/sessions?agent=${encodeURIComponent(agentId)}`, {
    headers: _skillsEngineHeaders(),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`engine ${resp.status}: ${detail || resp.statusText}`);
  }
  return resp.json() as Promise<{ sessions: ILsRagSession[]; count: number }>;
};

export const _skillsEngineCreateSession = async (agentId: string, name?: string): Promise<ILsRagSession> => {
  const engineBase = AppSettings.DCABackendServer || '';
  const resp = await fetch(`${engineBase}/api/sessions`, {
    method: 'POST',
    headers: _skillsEngineHeaders(),
    body: JSON.stringify({ agent: agentId, name }),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`engine ${resp.status}: ${detail || resp.statusText}`);
  }
  return resp.json() as Promise<ILsRagSession>;
};

export const HINTS = [
  'What cancers is Keytruda approved for?',
  'What is the starting dose of Ozempic?',
  'What are the side effects of Mounjaro?',
];

export const WELCOME: ILsRagMessage = {
  id: '__welcome__',
  session_id: '',
  role: 'assistant',
  content:
    "Hello! I'm the Scientific Literature FAQ agent. I answer questions grounded in indexed drug labels and scientific literature. Every answer includes inline citations and a real-time trust score. How can I help?",
  created_at: new Date().toISOString(),
};

// No agent names a specific model here — all 3 auto-route per query (see
// ModelBar's "Auto" chip), so a fixed "running on X" claim would go stale
// the moment routing picks a different tier for a given question.
export const AGENT_WELCOME: Record<string, string> = {
  'migration-assistant':
    "Hello! I'm the Migration Assistant — a multi-agent orchestrator that automatically routes each request to the most cost-effective model for the task. Ask me about migration planning, dependency analysis, wave sequencing, or lift-and-shift vs re-platform decisions. I'll coordinate sub-agents (Dependency Mapper, Cost Estimator, Risk Assessor, Wave Planner) and return a full migration plan.",
  'landing-zone-advisor':
    "Hello! I'm the Landing Zone Advisor. I automatically route each question to the most cost-effective model, with prompt caching for fast, consistent answers. Ask me anything about AWS/Azure/GCP landing zone architecture, account vending, SCPs, compliance guardrails, or network segmentation.",
  'devops-copilot':
    "Hello! I'm the DevOps Copilot. I automatically route each request to the most cost-effective model for the task. Paste your pipeline error, Terraform failure, or Kubernetes crash log and I'll give you the root cause and the fix — no preamble, just the answer.",
};

export const getWelcomeMsg = (agentIdx: number): ILsRagMessage => {
  const ag = AGENTS[agentIdx];
  const content = ag?.agentId && AGENT_WELCOME[ag.agentId] ? AGENT_WELCOME[ag.agentId] : WELCOME.content;
  return { ...WELCOME, content };
};
